import sortBy from 'lodash/sortBy';
import { useCallback, useState } from 'react';

import {
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_ACTIVITY,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentColumns,
  ComponentDocument,
  ComponentDraw,
  ComponentEbook,
  ComponentEbookInteractive,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentInfoBox,
  ComponentInteractive,
  ComponentLink,
  ComponentMultipleChoice,
  ComponentEquationResponse,
  ComponentResponse,
  ComponentSlideshow,
  ComponentTable,
  ComponentVideo,
  ComponentVocabulary,
  ComponentFillInBlanks,
  ComponentDiscussion,
  ComponentInteractiveResponseSequence,
  ComponentInteractiveResponseCategorise,
  ComponentRiveAnimation,
  ComponentGif,
  ComponentImageSphere,
} from './component.constants';
import InfoBoxView from './info-box/info-box-view';
import ResponsePrint from './response/response-print';
import MultipleChoiceView from './multiple-choice/multiple-choice-view';
import TableView from './table/table-view';
import EbookView from './ebook/ebook-view';
import DocumentView from './document/document-view';
import SlideshowView from './slideshow/slideshow-view';
import VideoView from './video/video-view';
import ImageView from './image/image-view';
import ImageSphereView from './image-sphere/image-sphere-view';
import InteractiveView from './interactive/interactive-view';
import InteractiveEbookView from './interactive-ebook/interactive-ebook-view';
import LinkView from './link/link-view';
import WebToolView from './webtool/webtool-view';
import GuidedResearchView from './guided-research/guided-research-view';
import ColumnsView from './columns/columns-view';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from '../activity-builder-zoom-component-modal';
import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import EquationResponseView from './equation-response/equation-response-view';
import VocabularyView from './vocabulary/vocabulary-view';
import FillInBlanksView from './fill-in-blanks/fill-in-blanks-view';
import DrawView from './draw/draw-view';
import DiscussionView from './discussion/discussion-view';
import InteractiveResponseView from './interactive-response/sequence/interactive-response-view';
import InteractiveResponseCategoriseView from './interactive-response/categorise/interactive-response-categorise-view';
import { SectionOrSubSection } from '../activity-builder.constants';
import ComponentsViewWrapper from './components-view-wrapper';
import { useActivityBuilderFlags } from '../activity-builder-flags';
import ResponseView from './response/response-view';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../present/activity-builder-present-data-tracking.utils';
import { EVENT_ACTION_EDIT } from 'approot/shared/data-tracker/data-events.constants';
import RiveView from './rive/rive-view';
import GifView from './gif/gif-view';
import { getStyleProperties } from './components.utils';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from './shared/component-settings-borders.utils';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  section: SectionOrSubSection;
  contentId: number;
  eventTrackingData?: ComponentEventData;
};

const ComponentsView = ({ section, contentId, eventTrackingData }: Props) => {
  const { activityBuilderRespondFlag } = useActivityBuilderFlags();

  const [zoomedComponent, setZoomedComponent] = useState<
    Pluck<InteractiveLessonSectionFragment, 'components'> | undefined
  >();

  const components = section?.components;
  const sortedComponents = sortBy(components, 'sort');

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: Pluck<InteractiveLessonSectionFragment, 'components'>,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      openModal();
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
    },
    [contentId, eventTrackingData, openModal]
  );

  if (!sortedComponents) return null;

  return (
    <div>
      {sortedComponents.map(component => {
        const openModalProps = {
          openModal: () => handleOpenModal(component),
        };
        return component.type !== ACTIVITIES_COMPONENT_TYPE_ACTIVITY ? (
          <ComponentsViewWrapper
            key={component.id}
            type={component.type}
            id={component.id}
            colours={getAppliedColour(getStyleProperties(component))}
            showBorder={isBorderApplied(getStyleProperties(component))}
            showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
              <InfoBoxView
                {...(component as ComponentInfoBox)}
                {...openModalProps}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
              <SlideshowView
                {...(component as ComponentSlideshow)}
                onComponentExpand={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {!activityBuilderRespondFlag &&
              component.type ===
                ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
                <ResponsePrint
                  {...(component as ComponentResponse)}
                  {...openModalProps}
                />
              )}
            {activityBuilderRespondFlag &&
              component.type ===
                ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
                <ResponseView
                  {...(component as ComponentResponse)}
                  {...openModalProps}
                />
              )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE && (
              <EquationResponseView
                {...(component as ComponentEquationResponse)}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE && (
              <MultipleChoiceView {...(component as ComponentMultipleChoice)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE && (
              <TableView {...(component as ComponentTable)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK && (
              <EbookView
                {...(component as ComponentEbook)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
              <DocumentView
                {...(component as ComponentDocument)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
              <VideoView
                {...(component as ComponentVideo)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
              <VideoView
                {...(component as ComponentAudio)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
              <ImageView
                {...(component as ComponentImage)}
                {...openModalProps}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
              <ImageSphereView {...(component as ComponentImageSphere)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
              <InteractiveView
                {...(component as ComponentInteractive)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
              <InteractiveEbookView
                {...(component as ComponentEbookInteractive)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS && (
              <ColumnsView
                {...(component as ComponentColumns)}
                contentId={contentId}
                eventTrackingData={eventTrackingData}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
              <LinkView
                {...(component as ComponentLink)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
              <WebToolView
                {...(component as ComponentLink)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
              <GuidedResearchView
                {...(component as ComponentGuidedResearch)}
                onStimulusOpen={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_STIMULUS_OPEN,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
              <VocabularyView
                {...(component as ComponentVocabulary)}
                onComponentExpand={({ vocabularyWord, vocabularyWordCount }) =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    eventTrackingData,
                    contentId,
                    vocabularyWord,
                    vocabularyWordCount,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE && (
              <FillInBlanksView {...(component as ComponentFillInBlanks)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW && (
              <DrawView
                {...(component as ComponentDraw)}
                {...openModalProps}
                onOpenDrawCanvas={() =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    expandMode: EVENT_ACTION_EDIT,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
              <DiscussionView
                {...(component as ComponentDiscussion)}
                onComponentExpand={expandMode =>
                  pushComponentViewEvent({
                    component,
                    eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
                    expandMode,
                    eventTrackingData,
                    contentId,
                  })
                }
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE && (
              <InteractiveResponseView
                {...(component as ComponentInteractiveResponseSequence)}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE && (
              <InteractiveResponseCategoriseView
                {...(component as ComponentInteractiveResponseCategorise)}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
              <RiveView {...(component as ComponentRiveAnimation)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
              <GifView {...(component as ComponentGif)} {...openModalProps} />
            )}
          </ComponentsViewWrapper>
        ) : null;
      })}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}
    </div>
  );
};

export default ComponentsView;
